import DangerArrowUp from "../../../../assets/up-arrow-danger.svg";
import DangerArrowUpSm from "../../../../assets/up-arrow-sm-danger.svg";
import WarnArrowUpSm from "../../../../assets/up-arrow-sm-warn.svg";
import SafeArrowDownSm from "../../../../assets/down-arrow-sm-safe.svg";
import DownloadIcon from "../../../../assets/download-green.svg";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { numberFormatter } from "../../../../utils/formatter";
import { RenegotiateVendorDetailsData } from "../../../../services/interfaces/IVendorQuoteAssessment";
import { appInsights } from "../../../../router/router";
import { downloadExcel } from "../../../../utils/helpers/excelHelpers";
import SortDefault from "../../../../assets/sort_default.svg";
import SortAsc from "../../../../assets/sort_asc.svg";
import SortDsc from "../../../../assets/sort_desc.svg";
import { useEffect, useState } from "react";

type Props = { data: RenegotiateVendorDetailsData; commodity: string };

const RenegotiateSummary = ({ data, commodity }: Props) => {
  const [sortColumn, setSortColumn] = useState("");
  const [sortType, setSortType] = useState();
  const [tableData, setTableData] = useState<any>([]);

  useEffect(() => {
    setTableData(data.data);
  }, [data.data]);

  const handleExcelDownload = async () => {
    const baseHeaders = [
      { header: "Commodity", key: "commodity", width: 10 },
      { header: "Supplier", key: "vendor", width: 10 },
      { header: "District", key: "district", width: 10 },
      // { header: "Last month purchased volume", key: "lmPurchaseVol", width: 10 },
      {
        header: "Target Cost ($/cft)'3/4 EMT",
        key: "target_cost",
        width: 10,
        style: { numFmt: "$#,##0.00" },
      },
      {
        header: "Latest Quote ($/cft)'3/4 EMT",
        key: "latest_quote",
        width: 10,
        style: { numFmt: "$#,##0.00" },
      },
      {
        header: "% Increase in Quote w.r.t. target cost",
        key: "percent_difference",
        width: 10,
        style: { numFmt: "0.00%" },
      },
      {
        header: "Impact $",
        key: "impact",
        width: 10,
        style: { numFmt: "$#,##0.00" },
      },
    ];

    let formattedExcelData = data?.data?.map((item: any) => {
      let row: any = {};
      baseHeaders.forEach((header: { key: any }) => {
        const key: any = header.key;
        if (key === "percent_difference") {
          row[key] = item[key];
        } else if (key === "commodity") {
          row[key] = commodity;
        } else {
          row[key] = item[key];
        }
      });
      return row;
    });

    await downloadExcel(
      formattedExcelData,
      baseHeaders,
      `Renegotiate Summary`,
      "Renegotiate Summary",
    );

    appInsights.trackEvent({
      name: "Vendor Quote Assessment: Renegotiate Summary",
      properties: {
        description: `${localStorage.getItem(
          "userEmail",
        )} has downloaded "Renegotiate Summary_${commodity}.xlsx" Report`,
      },
    });
  };

  const sortByColumnName = (
    data: any[],
    order: "asc" | "desc" | null,
    column: string,
  ) => {
    if (!order) return data;
    return [...data].sort((a, b) => {
      if (a[column] < b[column]) return order === "asc" ? -1 : 1;
      if (a[column] > b[column]) return order === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (columnName: string, sortType: any) => {
    const order: any = sortType ? (sortType === "asc" ? "desc" : null) : "asc";
    setSortColumn(columnName);
    setSortType(order);

    if (order) {
      setTableData(sortByColumnName(data.data, order, columnName));
    } else {
      setTableData(data.data); // original order
    }
  };

  const renderSortIcon = (field: any) => {
    const iconSize = 12;
    if (sortColumn === field && sortType === "asc") {
      return <img width={iconSize} height={iconSize} src={SortAsc} alt="asc" />;
    } else if (sortColumn === field && sortType === "desc") {
      return (
        <img width={iconSize} height={iconSize} src={SortDsc} alt="desc" />
      );
    } else {
      return (
        <img
          width={iconSize}
          height={iconSize}
          src={SortDefault}
          alt="default"
        />
      );
    }
  };

  return (
    <>
      <div className="flex justify-start items-start basis-[30%] w-full gap-4">
        <div className="shadow-module-card border border-[#bebebe] bg-white h-full w-full rounded-md p-pd13 flex flex-col justify-start items-start">
          <span className="font-bold text-[0.7vw]">
            Potential Cost Savings By Renegotiation
          </span>
          <span className="font-normal text-[0.6vw] text-[#858585]">
            Based on the totals in the Impact Column
          </span>
          <span className="mt-auto font-normal text-[2.5vw]">
            {data?.kpi?.potentialCostSaving
              ? numberFormatter(data?.kpi?.potentialCostSaving, 0, "$")
              : "-"}
          </span>
        </div>
        <div className="shadow-module-card border border-[#bebebe] bg-white h-full w-full rounded-md p-pd13 flex flex-col justify-between items-start">
          <span className="font-bold text-[0.7vw]">
            Average Increase in Quote
          </span>
          <span className="font-normal text-[0.6vw] text-[#858585]">
            w.r.t. target cost
          </span>
          <span className="mt-auto font-normal text-[2.5vw] flex justify-start items-center gap-2">
            <span className="text-[#BC2525]">
              {data?.kpi?.averageQuoteChange
                ? numberFormatter(
                    Math.abs(data?.kpi?.averageQuoteChange * 100),
                    2,
                  ) + "%"
                : "-"}
            </span>
            {data?.kpi?.averageQuoteChange ? (
              <img src={DangerArrowUp} alt="arrow" />
            ) : (
              <></>
            )}
          </span>
        </div>
      </div>

      <div className="w-full shadow-module-card border border-[#bebebe] bg-white p-pd13 font-bold text-fs16 rounded-md basis-[5%] flex items-normal">
        <div className="mr-[1vh]">Renegotiation Supplier Details</div>
        <div
          onClick={() => {
            handleExcelDownload();
          }}
          className="cursor-pointer flex items-center"
        >
          <img src={DownloadIcon} className="w-[1vw] h-[1vw]" />
        </div>
      </div>

      <div className="w-full basis-[60%]">
        <DataTable
          // sortField="impact"
          // sortOrder={-1}
          removableSort
          value={tableData}
          stripedRows
          scrollable
          scrollHeight="35vh"
          showGridlines
          emptyMessage="No Data available."
        >
          <Column
            field="vendor"
            // header="Vendor"
            header={() => (
              <div
                className="w-full flex justify-between items-center cursor-pointer"
                onClick={() => handleSort("vendor", sortType)}
              >
                <div>Supplier</div> <div>{renderSortIcon("vendor")}</div>
              </div>
            )}
          ></Column>
          <Column
            field="district"
            // header="District"
            header={() => (
              <div
                className="w-full flex justify-between items-center cursor-pointer"
                onClick={() => handleSort("district", sortType)}
              >
                <div>District</div> <div>{renderSortIcon("district")}</div>
              </div>
            )}
          ></Column>
          <Column
            field="target_cost"
            header={
              <div className="flex justify-between w-full">
                <span>Target Cost ($/cft)</span>
                <span className="text-deloitteGreen">{data.size_trade}</span>
              </div>
            }
            body={(rowdata) => {
              return (
                <span>{numberFormatter(rowdata.target_cost, 2, "$")}</span>
              );
            }}
          ></Column>

          <Column
            header={
              <div className="flex justify-between w-full">
                <span>Latest Quote ($/cft)</span>
                <span className="text-deloitteGreen">{data.size_trade}</span>
              </div>
            }
            body={(rowdata) => {
              return (
                <div className="flex justify-between w-full">
                  <span>{numberFormatter(rowdata.latest_quote, 2, "$")}</span>
                  <span
                    className={`flex flex-nowrap gap-1 ${
                      rowdata.percent_difference <= 0
                        ? "text-deloitteGreen"
                        : rowdata.percent_difference > 0.04
                          ? "text-alert"
                          : "text-warning"
                    }`}
                  >
                    {numberFormatter(
                      Math.abs(rowdata.percent_difference * 100),
                      1,
                    ) + "%"}
                    {rowdata.percent_difference <= 0 && (
                      <img // width={"80%"}
                        className={"w-[0.8vw]"}
                        src={SafeArrowDownSm}
                        alt="safe"
                      ></img>
                    )}

                    {rowdata.percent_difference > 0 &&
                      rowdata.percent_difference <= 0.04 && (
                        <img // width={"80%"}
                          className={"w-[0.8vw]"}
                          src={WarnArrowUpSm}
                          alt="warn"
                        ></img>
                      )}

                    {rowdata.percent_difference > 0.04 && (
                      <img
                        // width={"80%"}
                        className={"w-[0.8vw]"}
                        src={DangerArrowUpSm}
                        alt="danger"
                      ></img>
                    )}
                  </span>
                </div>
              );
            }}
          ></Column>

          <Column
            field="impact"
            // header="Impact $"
            header={() => (
              <div
                className="w-full flex justify-between items-center cursor-pointer"
                onClick={() => handleSort("impact", sortType)}
              >
                <div>Impact $</div> <div>{renderSortIcon("impact")}</div>
              </div>
            )}
            body={(rowdata) => {
              return <span>{numberFormatter(rowdata.impact, 2, "$")}</span>;
            }}
          ></Column>
        </DataTable>
      </div>
    </>
  );
};

export default RenegotiateSummary;
